import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { DirectionalHint } from "@fluentui/react";

import type { FunctionOrValue, UILocation } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";

import { AppTooltipHost } from "./AppTooltipHost";

/**
 * Initializes the tooltip host and returns a React tooltip ref that needs to be merged or attached to
 * your target component. This is the recommended way to use the app tooltips in your component.
 * In most cases you won't need to access the actual tooltipHost, but it's provided for rare cases via another ref.
 */
export function useAppTooltipHost<RefT = HTMLElement>(
  tooltipId: string,
  opts?: {
    label?: FunctionOrValue<string>;
    direction?: DirectionalHint;
    hoverElement?: HTMLElement;
    location?: UILocation;
  }
): {
  tooltipRef: React.Ref<RefT>;
  tooltipHostRef: React.MutableRefObject<AppTooltipHost | undefined>;
} {
  const tooltipHost = React.useRef<AppTooltipHost>();
  const context = React.useContext(AppContext);
  const targetOverflowSetItem = !!checkExperiment(
    context,
    ribbonExperiments.tooltipsTargetOverflowSetItemEnabled
  );

  const { label, direction, hoverElement, location } = opts || {};

  const setTooltipHost = React.useCallback(() => {
    if (
      tooltipTargetRef.current &&
      label &&
      context.updateTooltipsHidden &&
      context.setTooltipElement
    ) {
      tooltipHost.current = new AppTooltipHost({
        updateTooltipsHidden: context.updateTooltipsHidden,
        setTooltipElement: context.setTooltipElement,
        htmlElement: tooltipTargetRef.current,
        controlId: tooltipId,
        controlLabel: typeof label === "function" ? label() : label,
        tooltipDirection: direction,
        performSetOnNextFrame: true,
        targetOverflowSetItem,
        hoverElement,
        location,
      });
      tooltipHost.current.registerTooltipHostActions();
    }
    // On cleanup, remove the tooltip and unregister the tooltip host
    return () => {
      tooltipHost.current?.setTooltipUndefined();
      tooltipHost.current?.unregisterTooltipHostActions();
      tooltipHost.current = undefined;
    };
  }, [
    context.updateTooltipsHidden,
    context.setTooltipElement,
    tooltipId,
    label,
    direction,
    targetOverflowSetItem,
    hoverElement,
    location,
  ]);

  // Local variable (not used as an actual DOM element ref)
  const tooltipTargetRef = React.useRef<HTMLElement>();
  // We need to use a callback ref instead, because a regular ref won't notify us of changes, yet the tooltip host
  // depends on the changes of the tooltipTargetRef.current value to mount/unmount tooltips when the HTML node is available.
  const localCallbackRef: React.Ref<RefT> = React.useCallback(
    (node: HTMLElement | null) => {
      if (node) {
        // We don't use setState because it would lead to cascading updates once ref is mounted,
        // so we simply call the setTooltipHost function directly.
        tooltipTargetRef.current = node;
        setTooltipHost();
      }
    },
    [setTooltipHost]
  ) as React.Ref<RefT>;
  // Additionally, any changes to other props need to re-create the new tooltip
  React.useEffect(setTooltipHost, [setTooltipHost]);

  return { tooltipRef: localCallbackRef, tooltipHostRef: tooltipHost };
}
